<!-- Show progress spinner whenever the form is loading -->
@if (form?.options.isLoading) {
  <p-progressSpinner class="flex"></p-progressSpinner>
}

@if (!form?.options.isLoading) {
  <div class="flex flex-col gap-4">

    <!-- Form header -->
    @if (form.header) {
      <div class="p-form-widget-head">
        <p><strong>{{form.header}}</strong></p>
      </div>
    }

    <!-- Output the form fields -->
    @for (field of form.fields; track $index; let i = $index) {
      <div>

        <!-- Field header -->
        @if (field.header) {
          <div class="field-header">
            <p><strong>{{field.header}}</strong></p>
          </div>
        }

        @switch (field.fieldType) {

          <!-- Radio buttons -->
          @case ('radioButtonGroup') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <div>
                  @for (f of field.fields; track $index; let j = $index) {
                    @switch (f.fieldType) {
                      @case ('radioButton') {
                        <div class="radio-button-wrapper">
                          <div class="flex items-center">
                            <p-radio-button
                              [name]="f.name"
                              [inputId]="f.name + '_' + j"
                              [value]="f.value"
                              [formControl]="formGroup.controls[f.name]"
                            />
                            <label [for]="f.name + '_' + j" class="ml-2">{{ f.label }}</label>
                          </div>
                        </div>
                      }
                    }
                  }
                </div>
              </div>
            }
          }

          <!-- Text horizontal lists -->
          @case ('horizontalTextList') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <div class="flex justify-center items-start flex-col">
                  <div class="flex justify-center items-center">
                    @for (item of field.options?.list; track $index) {
                      <span class="horizontal-text-list m-2 {{ item.class }}">{{item.value}}</span>
                    }
                  </div>
                </div>
              </div>
            }
          }

          <!-- MultiSelects -->
          @case ('multiSelect') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <p-multiSelect
                  [options]="field.options?.options"
                  [formControl]="formGroup.controls[field.name]"
                  placeholder="Select"
                  appendTo="body">
                </p-multiSelect>
              </div>
            }
          }

          <!-- Input groups -->
          @case ('inputGroup') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <div class="with-suffix">
                  @for (subField of field.fields; track $index; let j = $index) {
                    <p-input-group>
                      @switch (subField.fieldType) {
                        @case ('input') {
                          <input pInputText
                                 class="w-full"
                                 [type]="subField.options?.type"
                                 [min]="subField.options?.min"
                                 [max]="subField.options?.max"
                                 [maxlength]="subField.options?.maxLength"
                                 [formControl]="formGroup.controls[subField.name]"
                                 (keypress)="inputGroupOnKeyPress(subField, $event)" />
                          @if (subField.options?.addon) {
                            <p-inputgroup-addon>{{subField.options?.addon}}</p-inputgroup-addon>
                          }
                          @if (formGroup.controls[subField.name].hasError('required') && subField.options?.showRequiredError) {
                            <small class="p-error">
                              {{getErrorMessage(subField, subField.name, 'required')}}
                            </small>
                          }
                          @if (formGroup.controls[subField.name].hasError('max') || formGroup.controls[subField.name].hasError('min')) {
                            <small class="p-error">
                              {{getErrorMessage(subField, subField.name, 'min')}}
                            </small>
                          }
                        }
                      }
                    </p-input-group>
                  }
                </div>
              </div>
            }
          }

          <!-- Calendars -->
          @case ('calendar') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <p-date-picker
                  appendTo="body"
                  [formControl]="formGroup.controls[field.name]"
                  [showIcon]="true">
                </p-date-picker>
              </div>
            }
          }

          <!-- Checkboxes -->
          @case ('checkbox') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <p-checkbox
                  binary="true"
                  [label]="field.options?.label"
                  [formControl]="formGroup.controls[field.name]">
                </p-checkbox>
              </div>
            }
          }

          <!-- Input masks -->
          @case ('inputMask') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <p-input-mask
                  mask="(999) 999-9999"
                  [formControl]="formGroup.controls[field.name]"
                  [unmask]="true"
                  [autoClear]="false">
                </p-input-mask>
              </div>
            }
          }

          <!-- Inputs -->
          @case ('input') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <input type="text"
                       class="w-full"
                       [class]="{'disabled-input': field.options?.disabled}"
                       pInputText
                       [pKeyFilter]="field.options?.pKeyFilter"
                       [attr.id]="form.id + ':' + field.id"
                       [formControl]="formGroup.controls[field.name]" />
              </div>
            }
          }

          <!-- Dropdowns -->
          @case ('dropdown') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <p-select
                  [options]="field.options?.options"
                  placeholder="Select"
                  appendTo="body"
                  [formControl]="formGroup.controls[field.name]">
                </p-select>
              </div>
            }
          }

          <!-- Textareas -->
          @case ('textarea') {
            @if (field.showIf ? field.showIf(formGroup) : true) {
              <div class="field">
                <label class="pr-4" [class]="{ 'field-required': isRequired(field) }">
                  {{field.label}}
                </label>
                <textarea class="resize-y w-full"
                          pInputTextarea
                          data-test-id="textarea"
                          [formControl]="formGroup.controls[field.name]"></textarea>
                @if (formGroup.controls[field.name].hasError('required') && field.options?.showRequiredError) {
                  <small class="p-error">
                    {{getErrorMessage(field, field.name, 'required')}}
                  </small>
                }
                @if (formGroup.controls[field.name].hasError('maxlength')) {
                  <small class="p-error">
                    {{getErrorMessage(field, field.name, 'maxlength')}}
                  </small>
                }
              </div>
            }
          }

        }
      </div>
    }

    <!-- If buttons are enabled show them -->
    @if (form?.options?.enableButtons) {
      <div class="button-wrapper">
        @if (form?.options?.accessViewPermissions) {
          <ng-container *accessView="form?.options.accessViewPermissions">
            @if (!form?.options.isExternal) {
              <div class="flex justify-end mt-2">
                @if (!formGroup.enabled) {
                  <button pButton
                          type="button"
                          label="Edit"
                          data-test-id="edit-button"
                          [disabled]="!form?.options?.canEdit"
                          (click)="edit()"></button>
                } @else {
                  <button pButton
                          type="button"
                          label="Cancel"
                          severity="info"
                          class="mr-4"
                          data-test-id="cancel-button"
                          (click)="cancel()"></button>
                  <button pButton
                          type="button"
                          label="Save Changes"
                          data-test-id="save-changes-button"
                          [disabled]="form?.options.isLoading || formGroup.invalid || formGroup.pristine"
                          (click)="save()"></button>
                }
              </div>
            }
          </ng-container>
        } @else {
          @if (!form?.options.isExternal) {
            <div class="flex justify-end mt-2">
              @if (!formGroup.enabled) {
                <button pButton
                        type="button"
                        label="Edit"
                        data-test-id="edit-button"
                        [disabled]="!form?.options?.canEdit"
                        (click)="edit()"></button>
              } @else {
                <button pButton
                        type="button"
                        label="Cancel"
                        severity="info"
                        class="mr-4"
                        data-test-id="cancel-button"
                        (click)="cancel()"></button>
                <button pButton
                        type="button"
                        label="Save Changes"
                        data-test-id="save-changes-button"
                        [disabled]="form?.options.isLoading || formGroup.invalid || formGroup.pristine"
                        (click)="save()"></button>
              }
            </div>
          }
        }
      </div>
    }

  </div>
}
